import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import type { Report } from '../api/report'
import { toastUtil } from '../../utils/toast'
import AdminSidebar from '../components/AdminSidebar'
import AdminBaseModal from '../modals/AdminBaseModal'
import AdminReportDetailModal from '../models/AdminReportDetailModal'

const AdminReports: React.FC = () => {
  const navigate = useNavigate()
  const [reports, setReports] = useState<Report[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [selectedReport, setSelectedReport] = useState<Report | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const fetchReports = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await adminApi.getReports({
        page: currentPage,
        per_page: 20,
        search: search || undefined,
      })
      setReports(response.data)
      setLastPage(response.last_page)
      setTotal(response.total)
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error('신고 목록을 불러오는데 실패했습니다')
      }
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, search, navigate])

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token')
    if (!adminToken) {
      navigate('/admin/login')
      return
    }
    fetchReports()
  }, [navigate, fetchReports])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchReports()
  }

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    navigate('/admin/login')
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getReasonLabel = (reason: string) => {
    const reasonMap: Record<string, string> = {
      spam: '스팸 또는 광고성 내용',
      inappropriate: '부적절한 내용',
      harassment: '욕설 또는 비방',
      other: '기타',
    }
    return reasonMap[reason] || reason
  }

  const handleViewDetail = (report: Report) => {
    setSelectedReport(report)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedReport(null)
  }

  return (
    <div className="min-h-screen bg-backgroundColorBg2 flex">
      <AdminSidebar />

      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-brandPrimary-10% sticky top-0 z-10">
          <div className="px-24px py-16px flex items-center justify-end">
            <button
              onClick={handleLogout}
              className="text-style-button2 text-textColorText2 hover:text-brown-80%-main transition-colors"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 px-24px py-32px">
          <div className="bg-white rounded-16px shadow-sm p-32px">
            <div className="flex items-center justify-between mb-24px">
              <h2 className="text-style-title2 text-textColorText2">
                신고 관리
              </h2>
              <div className="text-style-body2 text-textColorText2">
                총 {total.toLocaleString()}개
              </div>
            </div>

            <form onSubmit={handleSearch} className="mb-24px">
              <div className="flex gap-12px">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="신고 사유, 신고자명, 리뷰 내용으로 검색"
                  className={`
                  flex-1
                  py-12px
                  px-16px
                  border
                  border-brandPrimary-20%
                  rounded-8px
                  text-style-body1
                  text-textColorText2
                  bg-transparent
                  focus:outline-none
                  focus:border-brandPrimary
                  placeholder:text-textColorText2
                `}
                />
                <button
                  type="submit"
                  className="py-12px px-24px bg-4B3621-80% text-white rounded-8px text-style-button2 hover:bg-4B3621-80%-70% transition-colors"
                >
                  검색
                </button>
              </div>
            </form>

            {isLoading ? (
              <div className="flex justify-center items-center py-80px">
                <div className="text-style-body2 text-textColorText2">
                  로딩 중...
                </div>
              </div>
            ) : reports.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-80px">
                <div className="text-style-body2 text-textColorText2 mb-8px">
                  신고가 없습니다.
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-brandPrimary-10%">
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          ID
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          신고자
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          신고 사유
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          리뷰 내용
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          신고일
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          관리
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map((report) => (
                        <tr
                          key={report.id}
                          className="border-b border-brandPrimary-5% hover:bg-backgroundColorBg2 transition-colors"
                        >
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {report.id}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {report.user?.nickname ||
                              report.user?.username ||
                              '-'}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {getReasonLabel(report.reason)}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2 max-w-300px">
                            <div className="truncate">
                              {report.review?.review_text || '-'}
                            </div>
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {formatDate(report.created_at)}
                          </td>
                          <td className="py-12px px-16px">
                            <button
                              onClick={() => handleViewDetail(report)}
                              className={`
                                py-8px
                                px-16px
                                bg-4B3621-80%
                                text-white
                                rounded-8px
                                text-style-button2
                                hover:bg-4B3621-80%-70%
                                transition-colors
                              `}
                            >
                              상세보기
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {lastPage > 1 && (
                  <div className="flex items-center justify-center gap-8px mt-32px">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                      className={`
                      py-8px
                      px-16px
                      rounded-8px
                      text-style-button2
                      border
                      ${
                        currentPage === 1
                          ? 'border-brandPrimary-10% text-textColorText3 cursor-not-allowed'
                          : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                      }
                    `}
                    >
                      이전
                    </button>
                    <div className="text-style-body2 text-textColorText2">
                      {currentPage} / {lastPage}
                    </div>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(lastPage, prev + 1))
                      }
                      disabled={currentPage === lastPage}
                      className={`
                      py-8px
                      px-16px
                      rounded-8px
                      text-style-button2
                      border
                      ${
                        currentPage === lastPage
                          ? 'border-brandPrimary-10% text-textColorText3 cursor-not-allowed'
                          : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                      }
                    `}
                    >
                      다음
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      <AdminBaseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="신고 상세 정보"
        maxWidth="max-w-[800px]"
        overlayOpacity="bg-opacity-50"
      >
        {selectedReport && (
          <AdminReportDetailModal
            report={selectedReport}
            onClose={handleCloseModal}
            formatDate={formatDate}
            getReasonLabel={getReasonLabel}
          />
        )}
      </AdminBaseModal>
    </div>
  )
}

export default AdminReports
